import { useState, type CSSProperties, type HTMLAttributes, type MouseEvent, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectDto, TagDto, TaskDto } from '../api/types';
import {
  useCompleteTask,
  useUncompleteTask,
  useUpdateTask
} from '../hooks/queries';
import { formatDateTime, formatDueDate, formatDueTime, isOverdue } from '../utils/dates';
import { IconCheck, IconRecur, IconStar } from './icons';

interface TaskRowProps {
  task: TaskDto;
  projectsById: Map<string, ProjectDto>;
  tagsById: Map<string, TagDto>;
  muted?: boolean;
  selected?: boolean;
  showProject?: boolean;
  /** Priorytety: show the ICE score badge */
  showIce?: boolean;
  onOpen: (taskId: string) => void;
  style?: CSSProperties;
  /** dnd-kit: ref for the sortable node */
  innerRef?: Ref<HTMLDivElement>;
  /** dnd-kit: attributes + listeners spread onto the row */
  dragProps?: HTMLAttributes<HTMLDivElement>;
  /** dnd-kit: row is currently being dragged */
  dragging?: boolean;
}

const COMPLETE_DELAY_MS = 420;

export function TaskRow({
  task,
  projectsById,
  tagsById,
  muted = false,
  selected = false,
  showProject = true,
  showIce = false,
  onOpen,
  style,
  innerRef,
  dragProps,
  dragging = false
}: TaskRowProps) {
  const { t } = useTranslation();
  const completeTask = useCompleteTask();
  const uncompleteTask = useUncompleteTask();
  const updateTask = useUpdateTask();
  const [checking, setChecking] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const project = task.projectId ? projectsById.get(task.projectId) : undefined;
  const tags = task.tagIds
    .map((id) => tagsById.get(id))
    .filter((tag): tag is TagDto => Boolean(tag));

  const handleCheck = (event: MouseEvent) => {
    event.stopPropagation();
    if (muted) {
      uncompleteTask.mutate(task.id);
      return;
    }
    if (checking) return;
    setChecking(true);
    window.setTimeout(() => setLeaving(true), COMPLETE_DELAY_MS - 220);
    window.setTimeout(() => completeTask.mutate(task.id), COMPLETE_DELAY_MS);
  };

  const handleStar = (event: MouseEvent) => {
    event.stopPropagation();
    updateTask.mutate({ taskId: task.id, patch: { starred: !task.starred } });
  };

  const classNames = ['task-row'];
  if (muted) classNames.push('muted');
  if (selected) classNames.push('selected');
  if (leaving) classNames.push('leaving');
  if (dragProps) classNames.push('sortable');
  if (dragging) classNames.push('dragging');

  return (
    <div
      ref={innerRef}
      {...dragProps}
      className={classNames.join(' ')}
      style={style}
      onClick={() => onOpen(task.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(task.id);
      }}
    >
      <button
        type="button"
        className={`task-check${muted ? ' checked' : ''}${checking ? ' checking' : ''}`}
        onClick={handleCheck}
        aria-label={muted ? t('tasks.uncomplete') : t('tasks.complete')}
      >
        <IconCheck />
      </button>
      <div className="task-body">
        <div className="task-title">
          {task.recurrence && (
            <span title={t('tasks.recurring')}>
              <IconRecur />
            </span>
          )}
          {task.title}
        </div>
        <div className="task-meta">
          {muted && task.completedAt && (
            <span className="task-meta-done">
              {t('tasks.completedAt', { date: formatDateTime(task.completedAt) })}
            </span>
          )}
          {showProject && project && (
            <span className="task-meta-project">
              <span className="color-dot" style={{ background: project.color }} />
              <span>{project.name}</span>
            </span>
          )}
          {!muted && task.dueDate && (
            <span className={`task-meta-due${isOverdue(task.dueDate) ? ' overdue' : ''}`}>
              {formatDueDate(task.dueDate)}
              {task.dueTime ? ` · ${formatDueTime(task.dueTime)}` : ''}
            </span>
          )}
          {showIce && task.iceScore !== null && (
            <span
              className="task-meta-ice"
              title={t('ice.tooltip', {
                i: task.iceImportance,
                c: task.iceConfidence,
                e: task.iceEase
              })}
            >
              {t('ice.badge', { score: task.iceScore })}
            </span>
          )}
          {tags.map((tag) => (
            <span key={tag.id} className="tag-chip">
              <span className="color-dot" style={{ background: tag.color }} />
              {tag.name}
            </span>
          ))}
        </div>
      </div>
      {!muted && (
        <button
          type="button"
          className={`star-btn${task.starred ? ' starred' : ''}`}
          onClick={handleStar}
          aria-label={task.starred ? t('tasks.unstar') : t('tasks.star')}
          aria-pressed={task.starred}
        >
          <IconStar filled={task.starred} />
        </button>
      )}
    </div>
  );
}
